<template>
  <div dir="rtl" class="min-h-[100dvh] bg-slate-50 text-slate-900 font-sans">
    <div class="max-w-3xl mx-auto px-4 py-8">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-extrabold">ניהול פלטפורמה</h1>
          <p class="text-slate-500 text-sm mt-1">התחברות לחשבון עסק לצורך בדיקת תקלות ותמיכה</p>
        </div>
        <img src="../../../assets/img/login/logo-sign.svg" alt="logo" class="w-8 h-8" />
      </div>

      <!-- Search -->
      <div class="relative mb-4">
        <i class="pi pi-search absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
        <input
          v-model="search"
          type="text"
          placeholder="חיפוש לפי שם עסק, טלפון או מזהה..."
          class="w-full !bg-white !border !border-slate-200 !rounded-xl !py-3 !pr-10 !pl-4 focus:!outline-none focus:!border-primary transition-all"
          @input="onSearchInput"
        />
      </div>

      <!-- Forbidden -->
      <div v-if="forbidden" class="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-center">
        אין לך הרשאת מנהל פלטפורמה.
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="flex justify-center py-16">
        <i class="pi pi-spin pi-spinner text-3xl text-primary" />
      </div>

      <!-- Empty -->
      <div v-else-if="!businesses.length" class="text-center text-slate-400 py-16">
        לא נמצאו עסקים.
      </div>

      <!-- List -->
      <div v-else class="space-y-2">
        <div
          v-for="biz in businesses"
          :key="biz.bid"
          class="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-3 shadow-sm"
        >
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-bold truncate">{{ biz.title || 'ללא שם' }}</span>
              <span
                v-if="biz.isTestBusiness"
                class="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5"
              >test</span>
              <span
                v-if="!biz.active"
                class="text-[10px] font-bold uppercase bg-slate-200 text-slate-600 rounded px-1.5 py-0.5"
              >לא פעיל</span>
            </div>
            <div class="text-xs text-slate-500 truncate mt-0.5">
              <span v-if="biz.ownerName">{{ biz.ownerName }}</span>
              <span v-if="biz.ownerName && biz.phone"> · </span>
              <span v-if="biz.phone" dir="ltr">{{ biz.phone }}</span>
              <span class="text-slate-300"> · {{ biz.bid }}</span>
            </div>
          </div>
          <Button
            :loading="impersonatingBid === biz.bid"
            class="!bg-primary !border-0 !text-white !font-bold !rounded-lg !px-4 !py-2 !text-sm whitespace-nowrap"
            @click="impersonate(biz)"
          >
            <span v-if="impersonatingBid !== biz.bid">התחברות</span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import Session from 'supertokens-web-js/recipe/session';
import { useToast } from 'vue-toastification';
import { IPlatformBusinessListItem } from '@torim-app/types';
import { PlatformApi } from '@app/api/platform-api';
import { AuthRouteNames } from '@app/router/route-names';
import { useNavigation } from '@app/composables/navigation';

const toast = useToast();
const { goTo } = useNavigation();

const businesses = ref<IPlatformBusinessListItem[]>([]);
const loading = ref(true);
const forbidden = ref(false);
const search = ref('');
const impersonatingBid = ref<string | null>(null);
let searchTimer: ReturnType<typeof setTimeout> | null = null;

const load = async () => {
  loading.value = true;
  forbidden.value = false;
  try {
    businesses.value = await PlatformApi.listBusinesses(search.value);
  } catch (e) {
    if (e === 'FORBIDDEN') {
      forbidden.value = true;
    } else {
      toast.error('טעינת העסקים נכשלה');
    }
  } finally {
    loading.value = false;
  }
};

const onSearchInput = () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(load, 300);
};

const impersonate = async (biz: IPlatformBusinessListItem) => {
  impersonatingBid.value = biz.bid;
  try {
    await PlatformApi.impersonate(biz.bid);
    // Hard reload so every store re-initialises against the owner's session.
    window.location.assign('/admin');
  } catch (e) {
    impersonatingBid.value = null;
    toast.error(e === 'FORBIDDEN' ? 'אין הרשאה' : 'ההתחברות לעסק נכשלה');
  }
};

onMounted(async () => {
  if (!(await Session.doesSessionExist())) {
    goTo(AuthRouteNames.AdminLogin);
    return;
  }
  await load();
});
</script>
